import { useDispatch, useSelector } from "react-redux";
// import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useConfirmModal from "../useConfirmModal";
import useModals from "../useModals";
import { AppDispatch, RootState } from "../../store/store";
import {
    InventoryTransferCreateDTO,
    InventoryTransferDTO,
    InventoryTransferUpdateDTO,
} from "../../types/inventory/transfer/InventoryTransferTypes";
import InventoryTransferService from "../../services/inventory/transfer/InventoryTransferService";
import {
    clearFormErrors,
    closeApproveModal,
    closeCancelModal,
    closeConfirmModal,
    closeCreateModal,
    closeDetailModal,
    closeRejectModal,
    closeReverseModal,
    closeUpdateModal,
    openApproveModal,
    openCancelModal,
    openConfirmModal,
    openCreateModal,
    openDetailModal,
    openRejectModal,
    openReverseModal,
    openUpdateModal,
    resetForm,
    resetTransferSlice,
    setFormErrors,
    setIsSubmitting,
    setTransferForm,
    updateTransferField
} from "../../store/slices/inventory/inventoryTransferSlice";
import useApiErrorHandler from "../useApiErrorHandler";
import { validateInventoryTransferUpdate } from "../../features/inventory/transfer/inventoryTransferValidator";
import { useInventoryTransferQuery } from "./useInventoryTransferQuery";

export const useInventoryTransfer = () => {
    const dispatch: AppDispatch = useDispatch();
    const confirm = useConfirmModal();
    // const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { openModal } = useModals();
    const { handle } = useApiErrorHandler();

    const {
        form,
        formErrors,
        isSubmitting,
        showCreateModal,
        showUpdateModal,
        showDetailModal,
        showApproveModal,
        showConfirmModal,
        showCancelModal,
        showReverseModal,
        showRejectModal,
    } = useSelector((state: RootState) => state.inventoryTransfer);

    const {
        handlerUpdatePendingTransfer,
        handlerRemovePendingTransfer,
        handlerGetPendingCount
    } = useInventoryTransferQuery();

    // -------------------------------------------------------------------
    // 🟩 Crear traslado (encabezado) — el modal permanece abierto para
    // que el usuario agregue los productos a continuación.
    // -------------------------------------------------------------------
    const handlerCreate = async (payload: InventoryTransferCreateDTO) => {
        try {
            dispatch(setIsSubmitting(true));
            showLoadingIndicator();
            const response = await InventoryTransferService.create(payload);
            const {
                statusCode,
                type,
                title,
                message,
                data
            } = response;
            if (statusCode === 200 || statusCode === 201) {
                dispatch(setTransferForm(data));
                openModal(type, { title, message, });
                handlerGetPendingCount();
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setIsSubmitting(false));
            hideLoadingIndicator();
        }
    };

    // -------------------------------------------------------------------
    // ✏️ Actualizar traslado
    // -------------------------------------------------------------------
    const handlerUpdate = async (id: number, payload: InventoryTransferUpdateDTO) => {
        const errors = validateInventoryTransferUpdate(payload);

        if (Object.keys(errors).length > 0) {
            dispatch(setFormErrors(errors));
            return;
        }

        try {
            dispatch(setIsSubmitting(true));
            showLoadingIndicator();

            const response = await InventoryTransferService.update(id, payload);
            const { statusCode, data, type, title, message } = response;
            if (statusCode === 200) {
                openModal(type, { title, message, });
                dispatch(closeUpdateModal());
                handlerUpdatePendingTransfer(data);
            }
        } catch (error) {
            handle(error);
        } finally {
            dispatch(setIsSubmitting(false));
            hideLoadingIndicator();
        }
    };

    // -------------------------------------------------------------------
    // 🔍 Obtener detalle por ID
    // -------------------------------------------------------------------
    const handlerGetById = async (id: number) => {
        try {
            showLoadingIndicator();
            const response = await InventoryTransferService.getById(id);
            console.log(response)
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setTransferForm(data));
                dispatch(openUpdateModal())
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerOpen = async (id: number, mode: string) => {
        try {
            showLoadingIndicator();
            const response = await InventoryTransferService.getById(id);
            console.log(response)

            const { statusCode, data } = response;
            if (statusCode !== 200) return;

            // 🔁 "edit" reabre el mismo modal de creación (con la tabla de
            // productos) para poder CONTINUAR una solicitud que ya existe —
            // openCreateModal() resetea el form a uno vacío, así que hay que
            // cargar los datos reales DESPUÉS de abrirlo, no antes.
            if (mode === "edit") {
                dispatch(openCreateModal());
                dispatch(setTransferForm(data));
                return;
            }

            dispatch(setTransferForm(data));

            switch (mode) {
                case "approve":
                    dispatch(openApproveModal());
                    break;

                case "confirm":
                    dispatch(openConfirmModal());
                    break;

                case "cancel":
                    dispatch(openCancelModal());
                    break;

                case "reverse":
                    dispatch(openReverseModal());
                    break;

                case "reject":
                    dispatch(openRejectModal());
                    break;

                default:
                    dispatch(openDetailModal());
                    break;
            }

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };



    // -------------------------------------------------------------------
    // ✔ Aprobar
    // -------------------------------------------------------------------
    const handlerApprove = async (id: number) => {
        confirm({
            title: "¿Aprobar traslado?",
            message: "El traslado quedará listo para ser confirmado.",
            icon: "info",
            confirmText: "Aprobar",
            cancelText: "Cancelar",
            onConfirm: async () => {
                try {
                    showLoadingIndicator();
                    const response = await InventoryTransferService.approve(id);
                    console.log(response);
                    const { statusCode, data, type, title, message } = response;
                    if (statusCode === 200) {
                        openModal(type, { title, message, });
                        handlerUpdatePendingTransfer(data);
                        dispatch(closeApproveModal());
                        handlerGetPendingCount();
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    // -------------------------------------------------------------------
    // ✔ Confirmar (mueve stock)
    // -------------------------------------------------------------------
    const handlerConfirm = async (id: number) => {
        confirm({
            title: "¿Confirmar traslado?",
            message: "El stock será movido a la sucursal destino.",
            icon: "info",
            confirmText: "Confirmar",
            cancelText: "Cancelar",
            onConfirm: async () => {
                try {
                    showLoadingIndicator();
                    const response = await InventoryTransferService.confirm(id);
                    console.log(response);
                    const { statusCode, data, type, title, message } = response;
                    if (statusCode === 200) {
                        openModal(type, { title, message, });
                        handlerUpdatePendingTransfer(data);
                        dispatch(closeConfirmModal());
                        handlerGetPendingCount();
                        handlerRemovePendingTransfer(data?.id)
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    // -------------------------------------------------------------------
    // ❌ Rechazar
    // -------------------------------------------------------------------
    const handlerReject = async (id: number) => {
        confirm({
            title: "¿Rechazar traslado?",
            message: "Esta acción marcará el traslado como rechazado.",
            icon: "warning",
            confirmText: "Rechazar",
            cancelText: "Cancelar",
            onConfirm: async () => {
                try {
                    showLoadingIndicator();
                    const response = await InventoryTransferService.reject(id);
                    console.log(response);
                    const { statusCode, data, type, title, message } = response;
                    if (statusCode === 200) {
                        openModal(type, { title, message, });
                        handlerUpdatePendingTransfer(data);
                        dispatch(closeRejectModal());
                        handlerGetPendingCount();
                        handlerRemovePendingTransfer(data?.id)
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    // -------------------------------------------------------------------
    // 🚫 Cancelar
    // -------------------------------------------------------------------
    const handlerCancel = async (id: number) => {
        confirm({
            title: "¿Confirmar cancelación del traslado?",
            message:
                "Esta acción cerrará permanentemente la solicitud de traslado. " +
                "Una vez cancelado, no podrá reabrirse ni continuar el proceso.",
            icon: "warning",
            confirmText: "Cancelar traslado",
            cancelText: "Cerrar",
            onConfirm: async () => {
                try {
                    showLoadingIndicator();
                    const response = await InventoryTransferService.cancel(id);
                    console.log(response);
                    const { statusCode, data, type, title, message } = response;

                    if (statusCode === 200) {
                        openModal(type, { title, message, });
                        handlerUpdatePendingTransfer(data);
                        dispatch(closeCancelModal());
                        handlerGetPendingCount();
                        handlerRemovePendingTransfer(data?.id)
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    // -------------------------------------------------------------------
    // 🔄 Revertir traslado
    // -------------------------------------------------------------------
    const handlerReverse = async (id: number) => {
        confirm({
            title: "¿Revertir traslado?",
            message: "El stock volverá a la sucursal origen.",
            icon: "warning",
            confirmText: "Revertir",
            cancelText: "Cerrar",
            onConfirm: async () => {
                try {
                    showLoadingIndicator();
                    const response = await InventoryTransferService.reverse(id);
                    const { statusCode, data, type, title, message } = response;
                    if (statusCode === 200) {
                        openModal(type, { title, message, });
                        handlerUpdatePendingTransfer(data);
                        dispatch(closeReverseModal());
                        handlerGetPendingCount();
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    return {
        form,
        formErrors,
        isSubmitting,

        // Form
        updateField: (field: any, value: any) =>
            dispatch(updateTransferField({ field, value })),
        setForm: (data: InventoryTransferDTO) => dispatch(setTransferForm(data)),
        resetForm: () => dispatch(resetForm()),

        // Errores
        setErrors: (e: Record<string, string>) => dispatch(setFormErrors(e)),
        clearErrors: () => dispatch(clearFormErrors()),

        // Modales
        showCreateModal,
        showUpdateModal,
        showDetailModal,
        showApproveModal,
        showConfirmModal,
        showCancelModal,
        showReverseModal,
        showRejectModal,

        openCreateModal: () => dispatch(openCreateModal()),
        closeCreateModal: () => dispatch(closeCreateModal()),

        openUpdateModal: () => dispatch(openUpdateModal()),
        closeUpdateModal: () => dispatch(closeUpdateModal()),

        openDetailModal: () => dispatch(openDetailModal()),
        closeDetailModal: () => dispatch(closeDetailModal()),

        openApproveModal: () => dispatch(openApproveModal()),
        closeApproveModal: () => dispatch(closeApproveModal()),

        openConfirmModal: () => dispatch(openConfirmModal()),
        closeConfirmModal: () => dispatch(closeConfirmModal()),

        openCancelModal: () => dispatch(openCancelModal()),
        closeCancelModal: () => dispatch(closeCancelModal()),

        openReverseModal: () => dispatch(openReverseModal()),
        closeReverseModal: () => dispatch(closeReverseModal()),

        openRejectModal: () => dispatch(openRejectModal()),
        closeRejectModal: () => dispatch(closeRejectModal()),


        resetSlice: () => dispatch(resetTransferSlice()),

        // Actions
        handlerCreate,
        handlerUpdate,
        handlerGetById,
        handlerOpen,
        handlerApprove,
        handlerConfirm,
        handlerReject,
        handlerCancel,
        handlerReverse,
    };
};
